import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DocumentSnapshot } from "firebase/firestore";
import dreamWorldProvider from "../DreamWorldProvider";
import { LibraryItem } from "../types/library";
import { RootState } from "./store";

interface ProfileState {
  content: LibraryItem[];
  hasMore: boolean;
  loading: "idle" | "pending" | "succeeded" | "failed";
  lastVisible: DocumentSnapshot | null;
}

const initialState: ProfileState = {
  content: [],
  hasMore: true,
  loading: "idle",
  lastVisible: null,
};

export const getProfileContent = createAsyncThunk(
  "profile/getProfileContent",
  async (
    {
      userId,
      lastVisible,
    }: { userId: string; lastVisible: DocumentSnapshot | undefined },
    { getState }
  ) => {
    const { items, lastVisible: newLastVisible } =
      await dreamWorldProvider.getUserPublishedContent(userId, lastVisible);
    return { items, newLastVisible };
  }
);

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setProfileContent: (state, action: PayloadAction<LibraryItem[]>) => {
      state.content = action.payload;
    },
    updateProfileContent: (state, action: PayloadAction<LibraryItem>) => {
      const index = state.content.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1) {
        state.content[index] = action.payload;
      }
    },
    resetProfileContent: (state) => {
      state.content = [];
      state.hasMore = true;
      state.loading = "idle";
      state.lastVisible = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfileContent.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getProfileContent.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.content = [...state.content, ...action.payload.items];
        state.lastVisible = action.payload.newLastVisible;
        state.hasMore = !!action.payload.newLastVisible;
      })
      .addCase(getProfileContent.rejected, (state) => {
        state.loading = "failed";
      });
  },
});

export const {
  setProfileContent,
  updateProfileContent,
  resetProfileContent,
} = profileSlice.actions;

export const selectProfileContent = (state: RootState) =>
  state.profile.content;
export const selectProfileHasMore = (state: RootState) => state.profile.hasMore;
export const selectProfileLoading = (state: RootState) => state.profile.loading;
export const selectProfileLastVisible = (state: RootState) =>
  state.profile.lastVisible;

export default profileSlice.reducer;
